import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, AlertIcon, Badge, Box, Button, Container, Flex, Heading, HStack, IconButton,
  Input, InputGroup, InputLeftElement, Stack, Text, useToast,
} from '@chakra-ui/react';
import { DeleteIcon, SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { RootState } from '../redux/store';
import { addTracks, setReferenceId } from '../redux/features/localAudioSlice';
import {
  pause as playerPause, play as playerPlay, setQueue, expand as playerExpand, PlayerTrack,
} from '../redux/features/playerSlice';
import { useLibrary } from '../library/useLibrary';
import { searchSongs, generatorReferencePath } from '../catalog/api';

/**
 * Music library — device-local, IndexedDB-persistent, hybrid-import.
 *
 *   • Chrome/Edge (desktop + Android): tap "Import folder" once, the app
 *     remembers the folder handle, and re-scans automatically on every
 *     future visit. Rename/add/delete files in the folder → next visit
 *     the library mirrors it. This is the closest a browser can get to
 *     the native-music-player "scan the Music folder" experience.
 *
 *   • Safari / iOS / Firefox: `showDirectoryPicker` doesn't exist. Falls
 *     back to `<input webkitdirectory>` (bulk folder pick, one-time) and
 *     `<input type="file" multiple>` (individual files). Metadata is
 *     cached in IndexedDB so the list still renders instantly on repeat
 *     visits, but the user has to re-pick on every session for playback
 *     because the browser doesn't grant persistent file access.
 *
 *   • All platforms: files shared to MixGenie via the OS Share sheet
 *     land here too (see `sw.ts` + the /shared path).
 *
 * We store metadata only — no audio bytes — per the "metadata only"
 * initial choice. Playback needs a live `File` handle from the current
 * session; rows without one show a "Reconnect" affordance instead.
 */
export default function LibraryPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const referenceId = useSelector((s: RootState) => s.localAudio.referenceId);
  const [query, setQuery] = useState('');

  const {
    tracks, dirName, isScanning, lastScanAt, needsRegrant, error,
    supportsAutoSync, getFile, pickFolder, reconnectFolder, ingestFileList,
    removeTrack, clearAll,
  } = useLibrary();

  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Object URLs — cached per session, revoked on unmount so we don't leak
  // blob memory. Recomputed only when the set of live files changes.
  const objectUrls = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tracks) {
      const file = getFile(t.id);
      if (file) map.set(t.id, URL.createObjectURL(file));
    }
    return map;
  }, [tracks, getFile]);
  useEffect(() => () => {
    for (const url of objectUrls.values()) URL.revokeObjectURL(url);
  }, [objectUrls]);

  // Share Target inbox — drained when arriving at ?shared=1. Same flow
  // as before the redesign, kept unchanged.
  const cameFromShare = location.search.includes('shared=1');
  const [shareBanner, setShareBanner] = useState<null | 'ok' | 'empty' | 'error'>(() => {
    const p = new URLSearchParams(location.search).get('shared');
    return p === '1' || p === 'empty' || p === 'error' ? (p as 'ok' | 'empty' | 'error') : null;
  });
  useEffect(() => {
    if (!cameFromShare) return;
    (async () => {
      try {
        if (!('caches' in window)) return;
        const cache = await caches.open('mixgenie-shared-inbox');
        const keys = await cache.keys();
        const fresh: File[] = [];
        for (const key of keys) {
          const res = await cache.match(key);
          if (!res) continue;
          const blob = await res.blob();
          const name = res.headers.get('X-Mixgenie-Filename') || 'shared-audio';
          fresh.push(new File([blob], name, { type: blob.type || 'audio/mpeg' }));
          await cache.delete(key);
        }
        if (fresh.length) {
          const dt = new DataTransfer();
          for (const f of fresh) dt.items.add(f);
          await ingestFileList(dt.files);
        }
        navigate('/library', { replace: true });
      } catch { setShareBanner('error'); }
    })();
  }, [cameFromShare, ingestFileList, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tracks;
    return tracks.filter((tr) => {
      return (
        tr.title?.toLowerCase().includes(q) ||
        tr.artist?.toLowerCase().includes(q) ||
        tr.name.toLowerCase().includes(q)
      );
    });
  }, [tracks, query]);

  const playableTracks: PlayerTrack[] = useMemo(() => (
    filtered
      .filter((tr) => !!objectUrls.get(tr.id))
      .map((tr) => ({
        id: tr.id,
        title: tr.title || tr.name,
        artist: tr.artist || t('library.localTrack', 'Local file'),
        audioUrl: objectUrls.get(tr.id) as string,
      }))
  ), [filtered, objectUrls, t]);

  const player = useSelector((s: RootState) => s.player);
  const currentTrackId = player.index >= 0 ? player.queue[player.index]?.id : null;

  const playAt = (id: string) => {
    const idx = playableTracks.findIndex((tr) => tr.id === id);
    if (idx < 0) return;
    dispatch(setQueue({ tracks: playableTracks, startIndex: idx }));
  };

  // "Use for generation": try to identify the local track against the
  // backend catalog first. If we find a match, we hand the generator a
  // full reference (title/artist/slug/cover + iTunes preview server-side)
  // instead of the raw local file — Mureka gets richer seeding and the
  // Generator can show real cover art. If no match, fall back to the
  // local-file reference path (existing behavior).
  const useAsReference = async (id: string) => {
    const track = tracks.find((tr) => tr.id === id);
    if (!track) return;
    // The File handle lives in useLibrary's session-only ref map. Without it
    // the generator can't attach anything — the row shows "Reconnect" for a
    // reason, so surface that instead of navigating to a dead form.
    const file = getFile(id);
    if (!file) {
      toast({
        title: t('library.needsReconnect', 'Reconnect your library first'),
        description: t('library.needsReconnectHint', 'The audio file for this row isn\'t loaded in this session. Tap the folder chip above to grant access again.'),
        status: 'warning',
        duration: 3500,
      });
      return;
    }
    // Put the File in the shared redux slice BEFORE navigating — GeneratorPage
    // reads `localAudio.tracks[referenceId]` and displays the chip / attaches
    // the audio on submit. Without this add, the id we set below points at
    // nothing and the reference silently drops.
    dispatch(addTracks([{
      id: track.id,
      file,
      name: track.name,
      size: track.size,
      addedAt: Date.now(),
      title: track.title,
      artist: track.artist,
    }]));

    const query = [track.artist, track.title || track.name]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (query.length >= 2) {
      try {
        const res = await searchSongs(query, 1, 5);
        const best = pickBestMatch(res.results || [], track.title, track.artist);
        if (best) {
          // Catalog matched — send both: the metadata (title/artist/slug/cover
          // for the job record + reference chip), plus the local File via the
          // redux slice we just wrote. Backend prefers referenceAudio over the
          // iTunes preview, so the user's own file wins as the Mureka seed.
          dispatch(setReferenceId(id));
          navigate(generatorReferencePath(best));
          toast({
            title: t('library.matched', 'Matched in catalog'),
            description: `${best.songTitle}${best.artistName ? ' — ' + best.artistName : ''}`,
            status: 'success',
            duration: 2200,
          });
          return;
        }
      } catch { /* silent — fall through to local-file reference */ }
    }
    dispatch(setReferenceId(id));
    navigate('/generator?localRef=1');
  };

  return (
    <Container maxW="720px" py={{ base: 2, md: 4 }} px={0}>
      <Stack spacing={2} mb={5} align="flex-start">
        <HStack spacing={3} align="center" wrap="wrap">
          <Heading
            as="h1"
            fontSize={{ base: '28px', md: '34px' }}
            fontWeight={800}
            lineHeight="1.1"
            letterSpacing="-0.015em"
            color="whiteAlpha.900"
          >
            {t('library.title', 'Music library')}
          </Heading>
          <SyncPill isScanning={isScanning} lastScanAt={lastScanAt} needsRegrant={needsRegrant} dirName={dirName} />
        </HStack>
        <Text color="whiteAlpha.700" fontSize={{ base: 'sm', md: 'md' }}>
          {supportsAutoSync
            ? t('library.hintAutoSync', "Point MixGenie at your music folder — we'll remember it and re-scan every visit. Files stay on your device.")
            : t('library.hintPick', 'Pick audio files from your device. They stay in this browser tab only — nothing is uploaded until you use one as a reference.')}
        </Text>
      </Stack>

      <HStack spacing={2} mb={5} wrap="wrap">
        {supportsAutoSync ? (
          <Button
            size="sm"
            onClick={pickFolder}
            borderRadius="12px"
            bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
            color="white"
            fontWeight={700}
            boxShadow="0 6px 18px rgba(233,74,143,0.28)"
            _hover={{ bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)', transform: 'translateY(-1px)' }}
            isLoading={isScanning}
            loadingText={t('library.scanning', 'Scanning…')}
            data-cta="library:import-folder"
          >
            {dirName ? t('library.reimport', 'Change folder') : t('library.importFolder', 'Import music folder')}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => folderInputRef.current?.click()}
            borderRadius="12px"
            bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
            color="white"
            fontWeight={700}
            boxShadow="0 6px 18px rgba(233,74,143,0.28)"
            _hover={{ bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)', transform: 'translateY(-1px)' }}
            data-cta="library:pick-folder"
          >
            {t('library.pickFolder', 'Pick folder')}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          borderColor="whiteAlpha.300"
          color="whiteAlpha.800"
          onClick={() => filesInputRef.current?.click()}
          borderRadius="12px"
          _hover={{ borderColor: 'whiteAlpha.500', bg: 'whiteAlpha.100' }}
          data-cta="library:pick-files"
        >
          {t('library.pickFiles', 'Pick files')}
        </Button>
        {needsRegrant && (
          <Button
            size="sm"
            variant="outline"
            borderColor="pink.400"
            color="pink.200"
            onClick={reconnectFolder}
            borderRadius="12px"
            _hover={{ bg: 'whiteAlpha.100' }}
            data-cta="library:reconnect"
          >
            🔗 {t('library.reconnect', 'Reconnect folder')}
          </Button>
        )}
        {tracks.length > 0 && (
          <IconButton
            aria-label={t('library.clearAll', 'Clear library')}
            icon={<DeleteIcon />}
            size="sm"
            variant="ghost"
            color="whiteAlpha.600"
            _hover={{ color: 'red.300', bg: 'whiteAlpha.100' }}
            onClick={() => {
              if (confirm(t('library.confirmClear', 'Clear the whole library? Your device files are not deleted — MixGenie just forgets the list.'))) {
                clearAll();
                toast({ title: t('library.cleared', 'Library cleared'), status: 'info', duration: 2000 });
              }
            }}
          />
        )}
      </HStack>

      {/* Hidden inputs the fallback picker buttons trigger. */}
      <input
        ref={filesInputRef}
        type="file"
        accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac,.opus"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => ingestFileList(e.target.files)}
      />
      <input
        ref={folderInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is non-standard but widely supported
        webkitdirectory=""
        directory=""
        multiple
        style={{ display: 'none' }}
        onChange={(e) => ingestFileList(e.target.files)}
      />

      {error && (
        <Alert
          status="warning"
          borderRadius="12px"
          mb={4}
          bg="rgba(70, 45, 20, 0.6)"
          backdropFilter="blur(10px)"
          color="whiteAlpha.900"
          border="1px solid"
          borderColor="yellow.500"
          fontSize="sm"
        >
          <AlertIcon color="yellow.300" />
          {error}
        </Alert>
      )}

      {shareBanner === 'ok' && (
        <Alert status="success" borderRadius="12px" mb={4} bg="rgba(20, 50, 30, 0.6)" backdropFilter="blur(10px)" color="whiteAlpha.900" border="1px solid" borderColor="green.500" fontSize="sm">
          <AlertIcon color="green.300" />
          {t('library.sharedOk', 'Track added from your Share sheet.')}
        </Alert>
      )}
      {shareBanner === 'empty' && (
        <Alert status="warning" borderRadius="12px" mb={4} bg="rgba(70, 45, 20, 0.6)" backdropFilter="blur(10px)" color="whiteAlpha.900" border="1px solid" borderColor="yellow.500" fontSize="sm">
          <AlertIcon color="yellow.300" />
          {t('library.sharedEmpty', "Nothing was shared — that item didn't have an audio file.")}
        </Alert>
      )}
      {shareBanner === 'error' && (
        <Alert status="error" borderRadius="12px" mb={4} bg="rgba(60, 20, 30, 0.6)" backdropFilter="blur(10px)" color="whiteAlpha.900" border="1px solid" borderColor="red.500" fontSize="sm">
          <AlertIcon color="red.300" />
          {t('library.sharedError', 'Could not process the shared file.')}
        </Alert>
      )}

      {tracks.length === 0 && (
        <Box
          bg="rgba(20, 14, 36, 0.55)"
          backdropFilter="blur(14px)"
          border="1px solid"
          borderColor="whiteAlpha.150"
          borderRadius="18px"
          p={{ base: 6, md: 8 }}
          textAlign="center"
        >
          <Text color="whiteAlpha.700" fontSize={{ base: 'sm', md: 'md' }}>
            {t('library.empty', 'No music yet. Import your folder above to get started.')}
          </Text>
        </Box>
      )}

      {tracks.length > 0 && (
        <InputGroup mb={4}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="whiteAlpha.500" />
          </InputLeftElement>
          <Input
            placeholder={t('library.searchPlaceholder', 'Search title, artist, filename…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            bg="rgba(10, 6, 22, 0.55)"
            border="1px solid"
            borderColor="whiteAlpha.200"
            color="whiteAlpha.900"
            borderRadius="12px"
            _hover={{ borderColor: 'whiteAlpha.300' }}
            _focus={{ borderColor: 'pink.300', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
            _placeholder={{ color: 'whiteAlpha.400' }}
          />
        </InputGroup>
      )}

      <Stack spacing={2}>
        {filtered.map((track) => {
          const url = objectUrls.get(track.id);
          const isRef = referenceId === track.id;
          const isCurrent = currentTrackId === track.id;
          const isPlaying = isCurrent && player.isPlaying;
          const canPlay = !!url;
          return (
            <Box
              key={track.id}
              as={canPlay ? 'button' : 'div'}
              w="full"
              textAlign="left"
              onClick={canPlay ? () => {
                if (isCurrent) dispatch(isPlaying ? playerPause() : playerPlay());
                else playAt(track.id);
              } : undefined}
              onDoubleClick={canPlay ? () => {
                if (!isCurrent) playAt(track.id);
                dispatch(playerExpand());
              } : undefined}
              bg={isCurrent ? 'rgba(60, 30, 80, 0.75)' : 'rgba(20, 14, 36, 0.55)'}
              backdropFilter="blur(14px)"
              border="1px solid"
              borderColor={isCurrent ? 'pink.400' : isRef ? 'accent.400' : 'whiteAlpha.150'}
              borderRadius="16px"
              px={{ base: 3, md: 4 }}
              py={3}
              transition="background 0.15s, border-color 0.15s"
              cursor={canPlay ? 'pointer' : 'default'}
              boxShadow={isCurrent ? '0 12px 32px rgba(233,74,143,0.25)' : '0 4px 14px rgba(0,0,0,0.15)'}
              _hover={{
                bg: isCurrent ? 'rgba(60, 30, 80, 0.85)' : 'rgba(30, 20, 48, 0.7)',
                borderColor: isCurrent ? 'pink.300' : 'whiteAlpha.300',
              }}
            >
              <Flex align="center" gap={3}>
                <Box
                  position="relative"
                  flexShrink={0}
                  boxSize={{ base: '52px', md: '60px' }}
                  borderRadius="10px"
                  overflow="hidden"
                  bgGradient="linear(135deg, teal.500, blue.600)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                  fontSize="2xl"
                  opacity={canPlay ? 1 : 0.55}
                >
                  ♫
                  {canPlay && (
                    <Flex
                      position="absolute"
                      inset={0}
                      align="center"
                      justify="center"
                      bg="blackAlpha.500"
                      color="white"
                      opacity={isCurrent ? 1 : 0.8}
                      pointerEvents="none"
                    >
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d={isPlaying ? 'M6 5h4v14H6zM14 5h4v14h-4z' : 'M8 5v14l11-7z'} />
                      </svg>
                    </Flex>
                  )}
                </Box>
                <Box flex="1" minW={0}>
                  <Text color="whiteAlpha.900" fontWeight={700} noOfLines={1}>
                    {track.title || track.name}
                  </Text>
                  <HStack spacing={2} color="whiteAlpha.600" fontSize="xs" mt={0.5} wrap="wrap">
                    <Text noOfLines={1}>{track.artist || t('library.localTrack', 'Local file')}</Text>
                    <Text>· {(track.size / (1024 * 1024)).toFixed(1)} MB</Text>
                    {!canPlay && (
                      <Badge colorScheme="orange" fontSize="9px">
                        {t('library.cachedOnly', 'reconnect to play')}
                      </Badge>
                    )}
                    {isRef && <Badge colorScheme="pink" fontSize="9px">{t('library.reference', 'reference')}</Badge>}
                  </HStack>
                </Box>
                <HStack spacing={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                  {canPlay && (
                    <Button
                      size="xs"
                      variant="outline"
                      borderColor="pink.400"
                      color="pink.200"
                      onClick={() => useAsReference(track.id)}
                      borderRadius="8px"
                      _hover={{ bg: 'whiteAlpha.100' }}
                      data-cta="library:use-as-reference"
                    >
                      ✨ {t('library.useAsReference', 'Use for gen')}
                    </Button>
                  )}
                  <IconButton
                    aria-label={t('library.remove', 'Remove')}
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    color="whiteAlpha.600"
                    _hover={{ color: 'red.300', bg: 'whiteAlpha.100' }}
                    onClick={() => removeTrack(track.id)}
                  />
                </HStack>
              </Flex>
            </Box>
          );
        })}
      </Stack>
      {tracks.length > 0 && filtered.length === 0 && (
        <Text color="whiteAlpha.600" fontSize="sm" textAlign="center" py={4}>
          {t('library.noMatch', 'No matches for "{{query}}"', { query })}
        </Text>
      )}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Fuzzy match helper — the backend search is already good at finding a
// track by title+artist, but the top result isn't always the one we want
// (e.g. a cover version, or a title-only match with a different artist).
// Score by normalized title equality first, then by normalized artist
// containing the parsed-artist token, and only pick if both look right.
// ---------------------------------------------------------------------------
function normalize(s?: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function pickBestMatch(
  results: Array<{ songTitle?: string; artistName?: string; [k: string]: any }>,
  wantedTitle?: string,
  wantedArtist?: string,
): any | null {
  const nt = normalize(wantedTitle);
  const na = normalize(wantedArtist);
  if (!nt) return null;
  for (const r of results) {
    const rt = normalize(r.songTitle);
    const ra = normalize(r.artistName);
    const titleOk = rt === nt || rt.includes(nt) || nt.includes(rt);
    const artistOk = !na || !ra || ra === na || ra.includes(na) || na.includes(ra);
    if (titleOk && artistOk) return r;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Sync / status pill — mirrors the same-shape pill on /songs so the two
// pages feel like siblings. States: Scanning · Needs reconnect · Synced.
// ---------------------------------------------------------------------------
function SyncPill({
  isScanning,
  lastScanAt,
  needsRegrant,
  dirName,
}: {
  isScanning: boolean;
  lastScanAt: number;
  needsRegrant: boolean;
  dirName: string;
}) {
  let label = '';
  let color = 'whiteAlpha.700';
  let dotColor = 'whiteAlpha.500';
  if (isScanning) {
    label = 'Scanning';
    dotColor = 'pink.300';
  } else if (needsRegrant) {
    label = 'Tap to reconnect';
    color = 'orange.200';
    dotColor = 'orange.300';
  } else if (dirName && lastScanAt) {
    const diff = Date.now() - lastScanAt;
    if (diff < 60_000) label = `Synced · ${dirName}`;
    else if (diff < 3600_000) label = `Synced ${Math.floor(diff / 60_000)}m ago`;
    else label = `Synced ${Math.floor(diff / 3600_000)}h ago`;
    color = 'whiteAlpha.600';
    dotColor = 'green.300';
  } else {
    return null;
  }
  return (
    <HStack
      spacing={2}
      px={2.5}
      py={1}
      borderRadius="full"
      bg="rgba(20, 14, 36, 0.6)"
      border="1px solid"
      borderColor="whiteAlpha.150"
      fontFamily="ui-monospace, SF Mono, Menlo, monospace"
      fontSize="10px"
      letterSpacing="0.06em"
      textTransform="uppercase"
      color={color}
      aria-live="polite"
      maxW="260px"
    >
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg={dotColor}
        boxShadow={isScanning ? '0 0 8px currentColor' : undefined}
      />
      <Text as="span" noOfLines={1}>{label}</Text>
    </HStack>
  );
}
